import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { MessageSquare, HelpCircle, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useRealtimeSettings } from '@/hooks/useRealtimeSettings';
import { useFaqSearch, FaqItem } from '@/hooks/useFaqSearch';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';

interface QuickActionsProps {
  onFaqResult?: (result: string) => void;
  onClose?: () => void;
}

export const QuickActions = ({ onFaqResult, onClose }: QuickActionsProps) => {
  const [faqQuery, setFaqQuery] = useState('');
  const [faqResults, setFaqResults] = useState<FaqItem[]>([]);
  const [selectedResult, setSelectedResult] = useState<FaqItem | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();
  const { settings } = useRealtimeSettings();
  const { searchFaq } = useFaqSearch();
  const isMobile = useIsMobile();

  const handleWhatsApp = () => {
    const whatsappLink = settings.whatsappLink;
    if (!whatsappLink) {
      toast.error("Lien WhatsApp non configuré");
      return;
    }
    window.open(whatsappLink, '_blank');
  };

  const handleFaqSearch = (query: string) => {
    if (!query.trim()) {
      setFaqResults([]);
      setSelectedResult(null);
      return;
    }
    
    setIsSearching(true);
    try {
      // Recherche dynamique en temps réel
      const results = searchFaq(query);
      setFaqResults(results);
      setSelectedResult(null);
    } catch (error) {
      toast.error("Erreur lors de la recherche");
    } finally {
      setTimeout(() => setIsSearching(false), 300); // Petit délai pour l'effet visuel
    }
  };

  const handleInputChange = (value: string) => {
    setFaqQuery(value);
    handleFaqSearch(value);
  };

  const handleResultSelect = (result: FaqItem) => {
    setSelectedResult(result);
    onFaqResult?.(result.answer);
  };

  const popularSearches = ['personnalisation', 'délai', 'prix', 'domicile', 'animation', 'traiteur'];

  const handleContactClick = () => {
    navigate('/contact');
    onClose?.(); // Fermer la modale
  };

  return (
    <div className={`bg-white border-t ${isMobile ? 'p-2' : 'px-3 py-2.5'}`}>
      <div className={`flex ${isMobile ? 'gap-1 justify-center items-center' : 'gap-2 justify-center'}`}>
        <Dialog>
          <DialogTrigger asChild>
            <Button 
              variant="outline" 
              size={isMobile ? "sm" : "sm"} 
              className={`flex items-center justify-center rounded-full ${isMobile ? 'min-w-[2.5rem] h-9 px-2' : 'gap-1'}`}
            >
              <HelpCircle className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />
              {!isMobile && 'Poser une question'}
            </Button>
          </DialogTrigger>
          <DialogContent className="z-[10000]">
            <DialogHeader>
              <DialogTitle>Rechercher dans notre FAQ</DialogTitle>
              <DialogDescription>
                Trouvez rapidement les réponses à vos questions les plus fréquentes
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Barre de recherche améliorée */}
              <div className="relative">
                <Input
                  placeholder="Tapez votre question (ex: remboursement, délai, prix...)"
                  value={faqQuery}
                  onChange={(e) => handleInputChange(e.target.value)}
                  className="pr-10"
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  </div>
                )}
              </div>
              
              {/* Suggestions populaires */}
              {!faqQuery && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Recherches populaires :</p>
                  <div className="flex flex-wrap gap-2">
                    {popularSearches.map((search) => (
                      <button
                        key={search}
                        onClick={() => handleInputChange(search)}
                        className="text-xs bg-muted hover:bg-muted/80 px-3 py-1 rounded-full transition-colors"
                      >
                        {search}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Résultats de recherche */}
              {faqQuery && faqResults.length === 0 && !isSearching && (
                <div className="text-center py-4">
                  <p className="text-sm text-muted-foreground">Aucun résultat trouvé</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Essayez des mots-clés différents ou
                    <Button 
                      variant="link" 
                      size="sm" 
                      onClick={() => {
                        navigate('/contact');
                        onClose?.();
                      }}
                      className="p-0 h-auto ml-1"
                    >
                      contactez-nous directement
                    </Button>
                  </p>
                </div>
              )}
              
              {faqResults.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-muted-foreground">
                    {faqResults.length} résultat{faqResults.length > 1 ? 's' : ''} trouvé{faqResults.length > 1 ? 's' : ''}
                  </p>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {faqResults.map((result) => (
                      <div
                        key={result.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-all hover:shadow-sm ${
                          selectedResult?.id === result.id 
                            ? 'bg-primary/10 border-primary shadow-sm' 
                            : 'hover:bg-muted/30 border-border'
                        }`}
                        onClick={() => handleResultSelect(result)}
                      >
                        <h4 className="font-medium text-sm mb-1 text-foreground">{result.question}</h4>
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{result.answer}</p>
                        <div className="flex gap-1 flex-wrap">
                          {result.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="text-xs bg-muted/60 text-muted-foreground px-2 py-0.5 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Réponse sélectionnée */}
              {selectedResult && (
                <div className="p-4 bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg">
                  <h4 className="font-semibold text-sm mb-2 text-primary">{selectedResult.question}</h4>
                  <p className="text-sm text-foreground leading-relaxed">{selectedResult.answer}</p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-primary/10">
                    <span className="text-xs text-muted-foreground">
                      Catégorie: {selectedResult.type === 'particulier' ? 'Particuliers' : 'Entreprises'}
                    </span>
                    <Button 
                      variant="outline"
                      size="sm" 
                      onClick={() => navigate('/faq')}
                      className="text-xs h-7"
                    >
                      Voir toutes les FAQ
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <Button 
          variant="outline" 
          size={isMobile ? "sm" : "sm"}
          onClick={handleWhatsApp}
          className={`flex items-center justify-center rounded-full ${
            isMobile ? 'min-w-[2.5rem] h-9 px-2' : 'gap-1'
          }`}
        >
          <MessageSquare className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />
          {!isMobile && 'WhatsApp'}
        </Button>

        <Button 
          variant="outline" 
          size={isMobile ? "sm" : "sm"}
          onClick={handleContactClick}
          className={`flex items-center justify-center ${isMobile ? 'min-w-[2.5rem] h-9 px-2' : 'gap-1'}`}
        >
          <Mail className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />
          {!isMobile && 'Contact'}
        </Button>
      </div>
    </div>
  );
};